import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Visibility } from "@mui/icons-material";
import { format } from "date-fns";
import { commonTableOptions } from "../../../../constants";
import { amountConverter, handleError } from "../../../../utils/common";
import { useParams } from "react-router-dom";
import { API_RESULT, API_VERSION } from "../../../../enums/common.enum";
import ErrorBoundary from "../../../../components/Helpers/ErrorBoundary";
import useAxiosInstance from "../../../../hooks/useAxiosInstance";
import { PayNowModal } from "../../Modal/PayNowModal";

const today = new Date();
today.setHours(0, 0, 0, 0);

const DUE_FILTERS = [
  { label: "Due Today", value: "due_today" },
  { label: "Due Tomorrow", value: "due_tomorrow" },
  { label: "Due This Week", value: "due_weekly" },
];

const Dues = ({
  borrowerInfo,
  setBorrowerInfo,
  visitedTabs,
  updateVisitedTabs,
}: any) => {
  const privateInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const params = useParams();
  const activeBorrowerId = params?.borrowerId;
  const [openPayModal, setOpenPayModal] = useState(false);
  const [payInvoice, setPayInvoice] = useState<any>(null);
  const [selectedFilter, setSelectedFilter] = useState("due_today");

  const fetchInvoiceData = async (filter: string) => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V3}/borrowers/applications`,
        {
          params: {
            borrower_id: activeBorrowerId,
            filter,
          },
        },
      );

      if (result?.data?.status === API_RESULT.SUCCESS) {
        updateVisitedTabs(3, "add");
        setData(result.data.response || []);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData(selectedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  const cols: any = [
    {
      name: "invoice_id",
      label: "Invoice ID",
      options: {
        filter: false,
      },
    },
    {
      name: "payee_name",
      label: "Payee Name",
      options: {
        filter: false,
      },
    },
    {
      name: "invoice_date",
      label: "Date",
      options: {
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];
          return (
            <Stack>
              <Typography variant="body2">
                Invoice Date:{" "}
                <strong>
                  {row.invoice_due_date
                    ? format(new Date(row.invoice_date), "dd/MM/yyyy")
                    : "-"}
                </strong>
              </Typography>
              <Typography variant="body2">
                Due Date:{" "}
                <strong>
                  {row.invoice_due_date
                    ? format(new Date(row.invoice_due_date), "dd/MM/yyyy")
                    : "-"}
                </strong>
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: "invoice_amount",
      label: "Amount",
      options: {
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];
          return (
            <Stack>
              <Typography variant="body2">
                Invoice Amount:{" "}
                <strong>₹{amountConverter(row.invoice_amount)}</strong>
              </Typography>
              <Typography variant="body2">
                Payable Amount:{" "}
                <strong>₹{amountConverter(row.payable_amount)}</strong>
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: "invoice_added_at",
      label: "Invoice Added At",
      options: {
        filter: false,
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        customBodyRender: (value: string) => {
          const colorMap: any = {
            approved: { bg: "#C6F6D5", color: "#22543D" },
            pending: { bg: "#FEEBC8", color: "#7B341E" },
            paid: { bg: "#E6FFFA", color: "#065F46" },
            cancelled: { bg: "#FED7D7", color: "#822727" },
            rejected: { bg: "#FED7D7", color: "#822727" },
          };

          return (
            <Chip
              size="small"
              label={value.toUpperCase()}
              sx={{
                backgroundColor: colorMap[value]?.bg,
                color: colorMap[value]?.color,
                fontWeight: 500,
              }}
            />
          );
        },
      },
    },
    {
      name: "actions",
      label: "Action",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];

          return (
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(row.invoice_file_url, "_blank");
                }}
              >
                <Visibility fontSize="small" color="primary" />
              </IconButton>

              <Button
                size="small"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  setPayInvoice(row);
                  setOpenPayModal(true);
                }}
              >
                Pay Now
              </Button>
            </Stack>
          );
        },
      },
    },
  ];

  const options: any = {
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    searchPlaceholder: "Invoice ID/Payee Name",
    rowsPerPageOptions: [10, 50, 100, 200],
    rowsPerPage: 10,
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: true,
    renderExpandableRow: (rowData: any, rowMeta: any) => {
      const row = data[rowMeta.dataIndex];

      const isCancelledOrRejected = ["cancelled", "rejected"].includes(
        row.status?.toLowerCase(),
      );

      return (
        <tr>
          <td colSpan={rowData.length + 1}>
            <Box p={2} bgcolor="#F8F9FC">
              {isCancelledOrRejected ? (
                <Box
                  sx={{
                    border: "1px solid #FCA5A5",
                    backgroundColor: "#FEF2F2",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#B91C1C" }}
                  >
                    {row.status === "cancelled"
                      ? row.cancelled_reason || "Invoice was cancelled"
                      : row.reject_reason || "Invoice was rejected"}
                  </Typography>
                </Box>
              ) : (
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Beneficiary Details
                    </Typography>

                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        Name:{" "}
                        <b>
                          {row.beneficiary_details?.beneficiary_account_name ||
                            "--"}
                        </b>
                      </Typography>
                      <Typography variant="body2">
                        Bank:{" "}
                        {row.beneficiary_details?.beneficiary_bank_name || "--"}
                      </Typography>
                      <Typography variant="body2">
                        Account No:{" "}
                        {row.beneficiary_details?.beneficiary_account_no ||
                          "--"}
                      </Typography>
                      <Typography variant="body2">
                        IFSC:{" "}
                        {row.beneficiary_details?.beneficiary_ifsc_code || "--"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Payment Summary
                    </Typography>

                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        Status: <b>{row.payment_status || "--"}</b>
                      </Typography>
                      <Typography variant="body2">
                        Payment Mode: {row.payment_mode || "--"}
                      </Typography>
                      <Typography variant="body2">
                        Transaction ID: {row.transaction_id || "--"}
                      </Typography>
                      <Typography variant="body2">
                        Payment At:{" "}
                        {row.paid_at
                          ? format(new Date(row.payment_at), "dd/MM/yyyy")
                          : "--"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Repayment Details
                    </Typography>

                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        Repayment Amount: ₹
                        {amountConverter(row.emi_amount || 0)}
                      </Typography>
                      <Typography variant="body2">
                        Tenure: {row.repayment_tenure || "--"} days
                      </Typography>
                      <Typography variant="body2">
                        Start Date:{" "}
                        {row.emi_start_date
                          ? format(new Date(row.emi_start_date), "dd/MM/yyyy")
                          : "--"}
                      </Typography>
                      <Typography variant="body2">
                        End Date:{" "}
                        {row.emi_end_date
                          ? format(new Date(row.emi_end_date), "dd/MM/yyyy")
                          : "--"}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          </td>
        </tr>
      );
    },
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={2}>
        {DUE_FILTERS.map((item) => (
          <Button
            key={item.value}
            size="small"
            variant={selectedFilter === item.value ? "contained" : "outlined"}
            onClick={() => setSelectedFilter(item.value)}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      <ErrorBoundary>
        <MUIDataTable title="" data={data} columns={cols} options={options} />
      </ErrorBoundary>

      {openPayModal && payInvoice && (
        <PayNowModal
          open={openPayModal}
          invoice={payInvoice}
          onClose={() => {
            setOpenPayModal(false);
            setPayInvoice(null);
          }}
        />
      )}
    </Box>
  );
};

export default Dues;
